import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Container,
  Grid,
  Avatar,
  Stack,
  Chip,
  Paper,
  InputBase,
  Zoom,
  IconButton,
} from "@mui/material";
import {
  Search as SearchIcon,
  LocalOfferOutlined as TagIcon,
  TrendingUp as ViewsIcon,
} from "@mui/icons-material";
import axios from "axios";
import headerBg from "../../../../Assets/images/matching-plt-header-bg.png";

const VideoExplication = () => {
  const [videos, setVideos] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Tous");

  // Charger les vidéos depuis l'API
  useEffect(() => {
    const fetchVideos = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_APIURL}/public/usage-guides`,
        );
        setVideos(res.data.data || []);
      } catch (error) {
        console.error("Erreur lors du chargement des vidéos:", error);
      }
    };
    fetchVideos();
  }, []);

  // Extraire les catégories uniques
  const categories = useMemo(() => {
    const cats = videos.map((v) => v.category).filter(Boolean);
    return ["Tous", ...new Set(cats)];
  }, [videos]);

  // Filtrer et trier les vidéos
  const filteredVideos = useMemo(() => {
    return videos
      .filter(
        (v) => selectedCategory === "Tous" || v.category === selectedCategory,
      )
      .filter((v) => v.title?.toLowerCase().includes(searchTerm.toLowerCase()))
      .sort((a, b) => a.position - b.position);
  }, [videos, selectedCategory, searchTerm]);

  // Extraire l'ID YouTube
  const getYoutubeId = (url) => {
    if (!url) return null;
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        py: 8,
        backgroundImage: `url(${headerBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}>
      <Container maxWidth="lg">
        {/* --- HEADER --- */}
        <Stack
          direction={{ xs: "column", md: "row" }}
          spacing={4}
          alignItems="center"
          sx={{ mb: 8 }}>
          <Box sx={{ flex: 1 }}>
            <Typography
              variant="h2"
              sx={{
                fontWeight: 900,
                color: "#1A1D23",
                letterSpacing: "-2px",
                mb: 1,
              }}>
              Apprendre{" "}
              <span style={{ color: "#4AAE9B" }}>
                facilement notre plateforme
              </span>
            </Typography>
            <Typography variant="h6" sx={{ color: "#67748E", fontWeight: 400 }}>
              {filteredVideos.length} guides vidéo disponibles
            </Typography>
          </Box>

          <Paper
            elevation={0}
            sx={{
              p: "10px 20px",
              display: "flex",
              alignItems: "center",
              borderRadius: "20px",
              width: { xs: "100%", md: 400 },
              boxShadow: "0 10px 30px rgba(0,0,0,0.05)",
              border: "1px solid #E9EDF7",
            }}>
            <SearchIcon sx={{ color: "#A3AED0", mr: 1.5 }} />
            <InputBase
              placeholder="Chercher un sujet..."
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Paper>
        </Stack>

        {/* --- FILTRES CATÉGORIES --- */}
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", mb: 6 }}>
          {categories.map((cat) => (
            <Chip
              key={cat}
              label={cat}
              onClick={() => setSelectedCategory(cat)}
              sx={{
                px: 2,
                py: 2.5,
                fontSize: "0.95rem",
                fontWeight: 700,
                borderRadius: "15px",
                transition: "0.3s",
                bgcolor: selectedCategory === cat ? "#1A1D23" : "#fff",
                color: selectedCategory === cat ? "#fff" : "#67748E",
                boxShadow:
                  selectedCategory === cat
                    ? "0 10px 20px rgba(0,0,0,0.1)"
                    : "none",
                "&:hover": {
                  bgcolor: selectedCategory === cat ? "#1A1D23" : "#E9EDF7",
                },
              }}
            />
          ))}
        </Box>

        {/* --- GRILLE DE VIDÉOS --- */}
        <Grid container spacing={3}>
          {filteredVideos.map((video) => {
            const youtubeId = getYoutubeId(video.youtube_url);
            return (
              <Grid item xs={12} sm={6} md={4} key={video.id}>
                <Zoom in timeout={400}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      borderRadius: "30px",
                      bgcolor: "#fff",
                      border: "1px solid #E9EDF7",
                      height: "100%",
                      transition: "0.4s",
                      "&:hover": {
                        transform: "translateY(-10px)",
                        boxShadow: "0 30px 60px rgba(112, 144, 176, 0.15)",
                      },
                    }}>
                    {/* ZONE VIDÉO */}
                    <Box
                      sx={{
                        position: "relative",
                        pt: "56.25%",
                        borderRadius: "22px",
                        overflow: "hidden",
                        bgcolor: "#000",
                        mb: 2.5,
                      }}>
                      {video.video_type === "youtube" ? (
  youtubeId ? (
    <iframe
      src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=0&controls=1&rel=0&modestbranding=1&iv_load_policy=3`}
      style={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        border: 0,
      }}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
      allowFullScreen
    />
  ) : (
    <Box sx={{
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      height: "100%"
    }}>
      Vidéo indisponible
    </Box>
  )
) : (
  <video
    controls
    style={{
      position: "absolute",
      inset: 0,
      width: "100%",
      height: "100%",
      objectFit: "cover",
    }}
  >
    <source
      src={`${process.env.REACT_APP_APIURL}/storage/${video.video_path}`}
    />
  </video>
)}
                    </Box>

                    {/* INFOS VIDÉO */}
                    <Box sx={{ px: 1 }}>
                      <Stack direction="row" spacing={1} mb={1.5}>
                        <TagIcon sx={{ fontSize: 14, color: "#4318FF" }} />
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 800,
                            color: "#4318FF",
                            textTransform: "uppercase",
                          }}>
                          {video.category}
                        </Typography>
                      </Stack>

                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 800,
                          color: "#1B2559",
                          mb: 1,
                          lineHeight: 1.3,
                        }}>
                        {video.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          color: "#A3AED0",
                          mb: 3,
                          display: "-webkit-box",
                          WebkitLineClamp: 4,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}>
                        {video.description}
                      </Typography>

                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center">
                        <Stack direction="row" alignItems="center" spacing={1}>
                          <ViewsIcon sx={{ fontSize: 16, color: "#A3AED0" }} />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 600, color: "#A3AED0" }}>
                            {video.views || 0} vues
                          </Typography>
                        </Stack>
                      </Stack>
                    </Box>
                  </Paper>
                </Zoom>
              </Grid>
            );
          })}
        </Grid>
      </Container>
    </Box>
  );
};

export default VideoExplication;
